import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AudiencePanel } from "@/components/studio/AudiencePanel";
import { ContentPanel } from "@/components/studio/ContentPanel";
import { PortfolioPanel } from "@/components/studio/PortfolioPanel";
import { JournalPanel } from "@/components/studio/JournalPanel";
import { AdsPanel } from "@/components/studio/AdsPanel";
import { AffiliatePanel } from "@/components/studio/AffiliatePanel";
import { SettingsPanel } from "@/components/studio/SettingsPanel";
import { SupportPanel } from "@/components/studio/SupportPanel";
import { errorMessage } from "@/lib/api";
import {
  clearStudioToken,
  getOwnerSite,
  patchSite,
  setStudioToken,
  studioLogin,
  studioStatus,
  studioToken,
} from "@/lib/site";
import { t } from "@/i18n";

/* The owner's side of the site. One password, one document, eight panels.

   The panels edit a local draft and one Save writes it: an editor that saved
   on every keystroke would turn a half-written sentence into live copy. */

const TABS = [
  { id: "analytics", label: "studio.tab.analytics" },
  { id: "content", label: "studio.tab.content" },
  { id: "portfolio", label: "studio.tab.portfolio" },
  { id: "journal", label: "studio.tab.journal" },
  { id: "ads", label: "studio.tab.ads" },
  { id: "affiliates", label: "studio.tab.affiliates" },
  { id: "support", label: "studio.tab.support" },
  { id: "settings", label: "studio.tab.settings" },
];

/** Write into a nested draft by dotted path, without mutating anything. */
const setIn = (object, path, value) => {
  const [head, ...rest] = path.split(".");
  if (!rest.length) return { ...object, [head]: value };
  return { ...object, [head]: setIn(object[head] || {}, rest.join("."), value) };
};

const Gate = ({ onIn }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    studioStatus()
      .then((status) => setConfigured(status.configured))
      .catch(() => setConfigured(true));
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { token } = await studioLogin(password);
      setStudioToken(token);
      onIn();
    } catch (error) {
      toast.error(errorMessage(error, t("studio.locked")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className="surface w-full max-w-sm rounded-xl p-7">
        <p className="eyebrow">{t("studio.title")}</p>
        <h1 className="mt-2 text-2xl font-semibold">{t("app.name")}</h1>

        {configured ? (
          <>
            <p className="mt-2 text-[13px] text-[color:var(--ink-soft)]">{t("studio.locked")}</p>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("studio.password")}
              autoComplete="current-password"
              autoFocus
              data-testid="studio-password"
              className="mt-5 h-11"
            />
            <Button type="submit" disabled={busy} className="mt-4 w-full" data-testid="studio-enter">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("studio.enter")}
            </Button>
          </>
        ) : (
          <p className="mt-3 text-[13px] leading-relaxed" style={{ color: "var(--warning)" }}>
            {t("studio.notConfigured")}
          </p>
        )}

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-[12px] text-[color:var(--muted-ink)] hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("nav.home")}
        </Link>
      </form>
    </div>
  );
};

export default function Studio() {
  const [authed, setAuthed] = useState(Boolean(studioToken()));
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [tab, setTab] = useState("analytics");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authed) return;
    getOwnerSite()
      .then((doc) => {
        setDraft(doc);
        setSaved(doc);
      })
      .catch((error) => {
        // an expired or revoked token lands here; back to the door
        if (error?.response?.status === 401) {
          clearStudioToken();
          setAuthed(false);
        }
      });
  }, [authed]);

  const dirty = useMemo(
    () => draft && saved && JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved]
  );

  const set = (path, value) => setDraft((current) => setIn(current, path, value));

  const save = async () => {
    setBusy(true);
    try {
      const doc = await patchSite(draft);
      setDraft(doc);
      setSaved(doc);
      toast.success(t("studio.saved"));
    } catch (error) {
      toast.error(errorMessage(error, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    clearStudioToken();
    setAuthed(false);
    setDraft(null);
  };

  if (!authed) return <Gate onIn={() => setAuthed(true)} />;

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--muted-ink)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-[color:var(--line)] bg-[color:var(--paper-2)]">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-baseline gap-3">
            <Link to="/" className="font-display text-xl font-semibold">
              {t("app.name")}
            </Link>
            <span className="text-[12px] uppercase tracking-[0.14em] text-[color:var(--muted-ink)]">
              {t("studio.title")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* only the content panels write to the site document; the journal
                and the campaign studio save their own rows as they go */}
            {["content", "portfolio", "ads", "affiliates", "support", "settings"].includes(tab) ? (
              <Button onClick={save} disabled={!dirty || busy} size="sm" data-testid="studio-save">
                {busy ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="mr-2 h-3.5 w-3.5" />
                )}
                {dirty ? t("studio.save") : t("studio.saved")}
              </Button>
            ) : null}
            <button
              type="button"
              onClick={signOut}
              aria-label={t("studio.signOut")}
              title={t("studio.signOut")}
              className="rounded-full border p-2"
              style={{ borderColor: "var(--line)" }}
              data-testid="studio-sign-out"
            >
              <LogOut className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-[1180px] overflow-x-auto px-6">
          <div className="flex gap-1 pb-2">
            {TABS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setTab(entry.id)}
                aria-pressed={tab === entry.id}
                data-testid={`studio-tab-${entry.id}`}
                className="whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] transition-colors"
                style={{
                  backgroundColor: tab === entry.id ? "#ffffff" : "transparent",
                  color: tab === entry.id ? "var(--ink)" : "var(--muted-ink)",
                }}
              >
                {t(entry.label)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1180px] px-6 py-8">
        {tab === "analytics" ? <AudiencePanel /> : null}
        {tab === "content" ? <ContentPanel draft={draft} set={set} /> : null}
        {tab === "portfolio" ? <PortfolioPanel draft={draft} set={set} /> : null}
        {tab === "journal" ? <JournalPanel /> : null}
        {tab === "ads" ? <AdsPanel draft={draft} set={set} /> : null}
        {tab === "affiliates" ? <AffiliatePanel draft={draft} set={set} /> : null}
        {tab === "support" ? <SupportPanel draft={draft} set={set} /> : null}
        {tab === "settings" ? <SettingsPanel draft={draft} set={set} /> : null}
      </main>
    </div>
  );
}
